import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts'

interface TrendLineProps {
  data: Array<{
    date: string
    value: number
    eventName?: string
  }>
  unit?: string
  lowerIsBetter?: boolean
  height?: number
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  return `${months[date.getMonth()]} ${date.getFullYear()}`
}

interface TooltipPayloadItem {
  payload: {
    date: string
    value: number
    eventName?: string
  }
  value: number
}

interface CustomTooltipProps {
  active?: boolean
  payload?: TooltipPayloadItem[]
  unit?: string
}

function CustomTooltip({ active, payload, unit }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) {
    return null
  }

  const item = payload[0]
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 text-sm">
      {item.payload.eventName && (
        <p className="font-medium text-gray-900">{item.payload.eventName}</p>
      )}
      <p className="text-gray-700">
        {item.value}
        {unit ? ` ${unit}` : ''}
      </p>
      <p className="text-gray-500 text-xs">{formatDate(item.payload.date)}</p>
    </div>
  )
}

export default function TrendLine({
  data,
  unit,
  lowerIsBetter = false,
  height = 250,
}: TrendLineProps) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
        No trend data yet
      </div>
    )
  }

  const chartData = data.map(d => ({
    ...d,
    formattedDate: formatDate(d.date),
  }))

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
        <XAxis dataKey="formattedDate" tick={{ fontSize: 12, fill: '#6b7280' }} tickLine={false} />
        <YAxis
          reversed={lowerIsBetter}
          tick={{ fontSize: 12, fill: '#6b7280' }}
          tickLine={false}
          label={
            unit
              ? {
                  value: unit,
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12, fill: '#9ca3af' },
                }
              : undefined
          }
        />
        <Tooltip content={<CustomTooltip unit={unit} />} />
        <Line
          type="monotone"
          dataKey="value"
          stroke="#16a34a"
          strokeWidth={2}
          dot={{ fill: '#16a34a', r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}
